import type { FullLoadout, FullWeaponInfo, WeaponLoadout } from "../../../api/types";
import type { MetaIndex } from "../../../state/meta";
import { StatBar } from "../../components/StatBar";
import { cx } from "../../cx";
import { BAR_ROWS } from "./labels";
import { previewWeapon, type PreviewStats } from "./statPreview";

type Trend = "gain" | "loss" | "same";

interface NumberCell {
  label: string;
  text: string;
  trend: Trend;
  /** Tooltip explaining paired values. */
  hint?: string;
}

/** Trailing zeros trimmed: 21 -> "21", 0.170 -> "0.17". */
function num(n: number, digits = 1): string {
  return String(Number(n.toFixed(digits)));
}

function trendOf(base: number, now: number, higherIsBetter: boolean): Trend {
  if (Math.abs(now - base) < 1e-6) return "same";
  return now > base === higherIsBetter ? "gain" : "loss";
}

/** The numeric cells under the bars. Pairs (range, mobility, spread) are compared by their sum. */
function numberCells(base: PreviewStats, now: PreviewStats): NumberCell[] {
  return [
    { label: "Damage", text: `${num(now.dmgNear)}–${num(now.dmgFar)}`, trend: "same", hint: "Close range to far range, per hit" },
    {
      label: "Range",
      text: `${num(now.rangeNear)}–${num(now.rangeFar)} m`,
      trend: trendOf(base.rangeNear + base.rangeFar, now.rangeNear + now.rangeFar, true),
      hint: "Full damage up to the first value, minimum damage from the second",
    },
    { label: "Fire rate", text: `${num(now.rpm, 0)} rpm`, trend: "same" },
    { label: "Magazine", text: num(now.mag, 0), trend: trendOf(base.mag, now.mag, true) },
    { label: "Reload", text: `${num(now.reload, 2)} s`, trend: trendOf(base.reload, now.reload, false) },
    { label: "ADS time", text: `${num(now.adsTime, 2)} s`, trend: trendOf(base.adsTime, now.adsTime, false) },
    { label: "Headshot", text: `×${num(now.headMult, 2)}`, trend: "same" },
    {
      label: "Mobility",
      text: `${num(now.mobility, 2)} / ${num(now.adsMobility, 2)}`,
      trend: trendOf(base.mobility + base.adsMobility, now.mobility + now.adsMobility, true),
      hint: "Movement speed hip / aiming",
    },
    {
      label: "Spread",
      text: `${num(now.hipSpread, 2)} / ${num(now.adsSpread, 2)}°`,
      trend: trendOf(base.hipSpread + base.adsSpread, now.hipSpread + now.adsSpread, false),
      hint: "Bullet cone hip / aiming",
    },
  ];
}

interface WeaponPreviewProps {
  heading: string;
  weapon: FullWeaponInfo;
  choice: WeaponLoadout;
  loadout: FullLoadout;
  index: MetaIndex;
}

function WeaponPreview({ heading, weapon, choice, loadout, index }: WeaponPreviewProps) {
  const preview = previewWeapon(weapon, choice.attachments, loadout.perks);
  const base = previewWeapon(weapon, [], []);
  const parts = choice.attachments.map((id) => index.attachments.get(id)?.name ?? id);
  if (choice.camo !== "none") parts.push(`${index.camos.get(choice.camo)?.name ?? choice.camo} camo`);
  const detail = parts.join(", ");
  return (
    <section class="loadouts__pv" aria-label={`${heading} preview`}>
      <header class="loadouts__pv-head">
        <span class="eyebrow">{heading}</span>
        <span class="loadouts__pv-name">{weapon.name}</span>
      </header>
      <p class="loadouts__pv-sub" title={detail}>
        {detail || "No attachments"}
      </p>
      <div class="loadouts__bars">
        {BAR_ROWS.map(({ key, label }) => (
          <StatBar key={key} label={label} value={preview.bars[key]} compare={weapon.bars[key]} />
        ))}
      </div>
      <dl class="loadouts__nums">
        {numberCells(base.stats, preview.stats).map((cell) => (
          <div key={cell.label} class={cx("loadouts__num", cell.trend !== "same" && `is-${cell.trend}`)} title={cell.hint}>
            <dt>{cell.label}</dt>
            <dd>{cell.text}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function nameList(items: readonly ({ name: string } | undefined)[]): string {
  return items.map((item) => item?.name).filter(Boolean).join(", ");
}

export interface PreviewPanelProps {
  loadout: FullLoadout;
  index: MetaIndex;
}

/** Right column: live stats of both weapons (bars compare against the bare weapon) and a loadout summary. */
export function PreviewPanel({ loadout, index }: PreviewPanelProps) {
  const primary = index.weapons.get(loadout.primary.weapon);
  const secondary = index.weapons.get(loadout.secondary.weapon);
  const equipment = nameList([index.equipment.get(loadout.lethal), index.equipment.get(loadout.tactical)]);
  const perks = nameList(loadout.perks.map((id) => index.perks.get(id)));
  return (
    <>
      {primary && (
        <WeaponPreview heading="Primary" weapon={primary} choice={loadout.primary} loadout={loadout} index={index} />
      )}
      {secondary && (
        <WeaponPreview heading="Secondary" weapon={secondary} choice={loadout.secondary} loadout={loadout} index={index} />
      )}
      <p class="loadouts__note">
        Estimates from attachments and perks: green gains, red loses. The server has the final numbers.
      </p>
      <dl class="loadouts__sum" aria-label="Loadout summary">
        <dt>Primary</dt>
        <dd>{primary?.name ?? loadout.primary.weapon}</dd>
        <dt>Secondary</dt>
        <dd>{secondary?.name ?? loadout.secondary.weapon}</dd>
        <dt>Equipment</dt>
        <dd>{equipment || "None"}</dd>
        <dt>Perks</dt>
        <dd>{perks || "None chosen"}</dd>
      </dl>
    </>
  );
}
